import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal, ModalContent, ModalTitle, ModalFooter,
} from '@twnel/web-components';
import { INPUT_TYPES } from 'app-constants/inputTypes';
import { convertSpecToRete } from 'data/actions/specToRete';
import { getBotspecInfo } from 'data/selectors';
import { postBotspec } from 'data/actions/chatbots';
import { BOT_UPDATE } from 'utils/toastDispacher';
import { validateOfflineData } from 'utils/offlineDataValidator';
import AceEditorInput from './AceEditorInput';
import logger from 'utils/logger';

const SpecVisualizer = ({ onClose, specJson = '', editable = false }) => {
  const dispatch = useDispatch();
  const { botspec } = useSelector(getBotspecInfo);

  const [editedSpec, setEditedSpec] = useState(specJson);
  const [validationError, setValidationError] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const hideMetaFields = (spec) => {
    try {
      const specJSON = JSON.parse(spec);
      Object.keys(specJSON.actions || {}).forEach((key) => {
        const action = specJSON.actions[key];
        if (action.type === 'call_api' && action.eval?.integration) {
          delete action.eval.params;
          delete action.eval.headers;
        }
      });
      return JSON.stringify(specJSON, null, 2);
    } catch {
      return spec;
    }
  };

  const validateJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Required fields per backend SPEC schema (schemas.js)
      const requiredFields = [
        { key: 'version', type: 'string' },
        { key: 'variables', type: 'object' },
        { key: 'messages', type: 'object' },
        { key: 'entrypoint', type: 'string' },
        { key: 'transitions', type: 'object' },
        { key: 'actions', type: 'object' },
        { key: 'functions', type: 'object' },
      ];

      const missingField = requiredFields.find(
        ({ key }) => parsed[key] === undefined || parsed[key] === null,
      );
      if (missingField) {
        return `El spec_json debe tener "${missingField.key}"`;
      }

      const wrongTypeField = requiredFields.find(({ key, type }) => (
        type === 'string'
          ? typeof parsed[key] !== 'string'
          : typeof parsed[key] !== 'object' || Array.isArray(parsed[key])
      ));
      if (wrongTypeField) {
        return `El campo "${wrongTypeField.key}" debe ser de tipo ${wrongTypeField.type}`;
      }

      // Validate that all input.type values are known types
      const validInputTypes = new Set(Object.values(INPUT_TYPES));
      const unknownInputTypes = [];
      Object.keys(parsed.actions).forEach((key) => {
        const action = parsed.actions[key];
        if (action.type === 'send_message' && action.input && action.input.type) {
          if (!validInputTypes.has(action.input.type)) {
            unknownInputTypes.push(`"${action.input.type}" (acción: ${key})`);
          }
        }
      });
      if (unknownInputTypes.length > 0) {
        return `Tipo(s) de input desconocido(s): ${unknownInputTypes.join(', ')}. `
          + `Tipos válidos: ${[...validInputTypes].join(', ')}`;
      }

      const offlineDataErrors = validateOfflineData(parsed);
      if (offlineDataErrors.length > 0) {
        return `offline_data inválido: ${offlineDataErrors.join('; ')}`;
      }

      return null;
    } catch (e) {
      return `JSON inválido: ${e.message}`;
    }
  };

  const handleEditorChange = (event) => {
    const newValue = event.target.value;
    setEditedSpec(newValue);
    setValidationError(null);

    // Validar en tiempo real
    const validation = validateJSON(newValue);
    if (validation) {
      setValidationError(validation);
    }
  };

  const handleSave = async () => {
    setError(null);

    // Validar antes de guardar
    const validation = validateJSON(editedSpec);
    if (validation) {
      setError(validation);
      return;
    }

    setSaving(true);

    try {
      const specJsonParsed = JSON.parse(editedSpec);

      // 1. Convertir spec_json a rete_json
      const newReteJson = convertSpecToRete(specJsonParsed, botspec.rete_json);

      // 2. Guardar ambos (spec_json editado y rete_json regenerado)
      await dispatch(
        postBotspec({
          ...botspec,
          spec_json: specJsonParsed,
          rete_json: newReteJson,
        }, {
          actionId: BOT_UPDATE,
          elementName: botspec.name,
        }),
      );

      // El toast de BOT_UPDATE (en postBotspec) confirma el guardado; cerramos el
      // modal y dejamos que el editor visual se reconstruya desde el rete_json.
      setSaving(false);
      onClose(true);
    } catch (err) {
      logger.error('Error al guardar spec:', err);
      const errorMessage = err.message || 'Error al guardar el spec_json';
      setError(errorMessage);
      setSaving(false);
    }
  };

  const displayValue = editable ? editedSpec : hideMetaFields(specJson);

  return (
    <Modal height="90%" width={900} onClose={onClose} modalStatic>
      <ModalTitle data-testid="modal-title">
        {editable ? '✏️ Editar Spec JSON' : 'Spec Visualizer'}
      </ModalTitle>
      <ModalContent>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {editable && (
            <div
              style={{
                padding: '10px',
                backgroundColor: '#fff3cd',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '13px',
              }}
            >
              ⚠️ Al guardar, el rete_json se regenerará automáticamente desde este spec_json.
            </div>
          )}

          {saving && (
            <div
              style={{
                padding: '10px',
                backgroundColor: '#d1ecf1',
                color: '#0c5460',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '13px',
                fontWeight: 'bold',
              }}
            >
              💾 Guardando... Por favor espera.
            </div>
          )}

          {validationError && (
            <div
              style={{
                padding: '10px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '13px',
              }}
            >
              {`❌ ${validationError}`}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '13px',
              }}
            >
              {`❌ Error: ${error}`}
            </div>
          )}

          <div style={{ flex: 1, minHeight: 0 }}>
            <AceEditorInput
              height="100%"
              value={displayValue}
              disabled={!editable}
              onChangeContent={editable ? handleEditorChange : undefined}
            />
          </div>
        </div>
      </ModalContent>

      {editable && (
        <ModalFooter>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '10px 0',
            }}
          >
            <div style={{ fontSize: '12px', color: '#666' }}>
              {saving ? '💾 Guardando...' : ''}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || validationError}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (saving || validationError) ? 'not-allowed' : 'pointer',
                  opacity: (saving || validationError) ? 0.6 : 1,
                }}
                title="Guardar y regenerar rete_json automáticamente"
              >
                {saving ? '💾 Guardando...' : '💾 Guardar y Regenerar Rete'}
              </button>
            </div>
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
};

SpecVisualizer.propTypes = {
  onClose: PropTypes.func.isRequired,
  specJson: PropTypes.string,
  editable: PropTypes.bool,
};

export default SpecVisualizer;
